import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Paper, Progress, Stack, Text, Title, createStyles} from '@mantine/core';
import React from 'react';

import {User} from 'constants/UserConstants';
import {derivePredictionUsage} from 'model-hub/lib/predictionUsage';
import {useCurrentUserAiCreditUsage} from 'model-hub/lib/useCurrentUserAiCreditUsage';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import EntityStore from 'stores/EntityStore';

// Read-only account page reached from the sidebar profile menu. Renders the fields we can source
// directly from the signed-in user (name, email) plus prediction usage. Editing, security, and
// data-management sections from the design are intentionally out of scope for this MVP.
export default function AccountPage(): JSX.Element | null {
  const {classes} = useStyles();
  const currentUser = EntityStore.currentUser() as User | null;

  // ModelHubApp only renders this route for signed-in users; render nothing defensively otherwise.
  if (currentUser == null) {
    return null;
  }

  // Mirror ModelHubProfileSection's fallback: name || handle, then email. Use `||` (not `??`) so an
  // empty display string also falls back to the email.
  const displayName = getPartyDisplayString(currentUser) || currentUser.email;

  return (
    <Stack
      className={classes.content}
      spacing="xl"
      {...getTestProps({component: 'AccountPage', element: 'root'})}
    >
      <Stack spacing={4}>
        <Title order={1}>Account</Title>
        <Text color="dimmed" size="sm">
          Your email is{' '}
          <Text span={true} className={classes.emailValue} fw={600}>
            {currentUser.email}
          </Text>
        </Text>
      </Stack>

      <ProfileSection displayName={displayName} email={currentUser.email} />
      <UsageSection />
    </Stack>
  );
}

function ProfileSection({displayName, email}: {displayName: string; email: string}): JSX.Element {
  const {classes} = useStyles();
  const initials = getInitials(displayName);

  return (
    <section aria-label="Profile">
      <SectionTitle>Profile</SectionTitle>
      <Paper className={classes.card} p="lg" withBorder={true}>
        <Stack spacing="lg">
          <div className={classes.identity}>
            <div className={classes.avatar} aria-hidden={true}>
              {initials}
            </div>
            <Stack spacing={2}>
              <Text fw={700} size="lg">
                {displayName}
              </Text>
              <Text color="dimmed" size="sm">
                {email}
              </Text>
            </Stack>
          </div>
          <div className={classes.fields}>
            <ReadOnlyField label="Name" value={displayName} />
            <ReadOnlyField label="Email" value={email} />
          </div>
        </Stack>
      </Paper>
    </section>
  );
}

function UsageSection(): JSX.Element {
  const {classes} = useStyles();
  const usageState = derivePredictionUsage(useCurrentUserAiCreditUsage());

  return (
    <section aria-label="Usage">
      <SectionTitle>Usage</SectionTitle>
      <Paper className={classes.card} p="lg" withBorder={true}>
        {usageState.status === 'loading' ? null : usageState.status === 'unavailable' ? (
          <Alert role="alert" variant="warning">
            No predictions granted — contact Benchling for help
          </Alert>
        ) : (
          <Stack spacing="sm" {...getTestProps({component: 'AccountPage', element: 'usage'})}>
            <div className={classes.usageRow}>
              <Text>
                <Text span={true} fw={700} size="xl">
                  {usageState.usage.predictionsUsed}
                </Text>{' '}
                <Text span={true} color="dimmed">
                  of {usageState.usage.predictionsGranted} predictions this month
                </Text>
              </Text>
            </div>
            <Progress
              aria-label="Predictions used this month"
              color="blue"
              radius="xl"
              size="sm"
              value={usageState.usage.usagePercentage}
            />
          </Stack>
        )}
      </Paper>
    </section>
  );
}

function ReadOnlyField({label, value}: {label: string; value: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div>
      <Text className={classes.fieldLabel}>{label}</Text>
      <Text className={classes.fieldValue}>{value}</Text>
    </div>
  );
}

function SectionTitle({children}: {children: React.ReactNode}): JSX.Element {
  const {classes} = useStyles();
  return <Text className={classes.sectionTitle}>{children}</Text>;
}

// Up to two initials from the leading words of the display name, upper-cased. Falls back to 'A'
// (matching the design) when nothing usable is present.
function getInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter((part) => part !== '')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return initials || 'A';
}

const useStyles = createStyles((theme) => ({
  content: {
    margin: '0 auto',
    maxWidth: 720,
    width: '100%',
  },
  emailValue: {
    color: theme.colors.gray[9],
  },
  sectionTitle: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
    fontWeight: 700,
    letterSpacing: '0.05em',
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  card: {
    borderColor: theme.colors.gray[3],
  },
  identity: {
    alignItems: 'center',
    display: 'flex',
    gap: 15,
  },
  avatar: {
    alignItems: 'center',
    background: `linear-gradient(135deg, ${theme.colors.grape[6]}, ${theme.colors.violet[6]})`,
    borderRadius: '50%',
    color: theme.white,
    display: 'flex',
    flex: '0 0 56px',
    fontSize: 19,
    fontWeight: 700,
    height: 56,
    justifyContent: 'center',
    width: 56,
  },
  fields: {
    display: 'grid',
    gap: theme.spacing.md,
    gridTemplateColumns: '1fr 1fr',
  },
  fieldLabel: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    marginBottom: theme.spacing.xs,
  },
  fieldValue: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.lg,
  },
  usageRow: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
  },
}));
